// src/components/Currency/MultiCurrencyDashboard.js
// F8: Multi-currency wallet — Wise-style consolidated balance dashboard
// Shows each account in its native currency + converted to homeCurrency.
// Total at bottom: consolidated balance in homeCurrency.

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
  IconButton,
  Stack
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import api from '../../services/api';
import { useAccount } from '../../contexts/AccountContext';

// Currency symbol helper
const CURRENCY_SYMBOLS = {
  EUR: '€', USD: '$', GBP: '£', JPY: '¥', CHF: 'Fr',
  CAD: 'CA$', AUD: 'A$', SEK: 'kr', NOK: 'kr', DKK: 'kr',
  PLN: 'zł', CZK: 'Kč', HUF: 'Ft', BRL: 'R$', CNY: '¥',
  INR: '₹', KRW: '₩', SGD: 'S$', NZD: 'NZ$'
};

const formatAmount = (amount, currency) => {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: currency === 'JPY' || currency === 'KRW' ? 0 : 2
    }).format(amount);
  } catch {
    const sym = CURRENCY_SYMBOLS[currency] || `${currency} `;
    return `${sym}${Number(amount).toFixed(2)}`;
  }
};

// Account type → display colour mapping (mirrors existing Account UI colours)
const ACCOUNT_TYPE_COLORS = {
  checking: 'primary',
  savings: 'success',
  ewallet: 'secondary',
  cash: 'warning',
  credit: 'error',
  creditcard: 'error',
  investment: 'info',
  loan: 'error',
  mortgage: 'error'
};

// Group array of accounts by their currency field
const groupByCurrency = (accounts) => {
  return accounts.reduce((groups, account) => {
    const key = account.currency || 'EUR';
    if (!groups[key]) groups[key] = [];
    groups[key].push(account);
    return groups;
  }, {});
};

const MultiCurrencyDashboard = () => {
  // Single source of truth — share the same accounts list as AccountsPage
  // (was previously a separate /accounts fetch that could fail independently
  // and render an "empty wallet" alongside populated account cards).
  const {
    accounts,
    loading: accountsLoading,
    error: accountsError,
  } = useAccount();

  const [homeCurrency, setHomeCurrency] = useState(
    () => localStorage.getItem('sk_homeCurrency') || 'EUR'
  );
  const [rates, setRates]             = useState({});
  const [fxLoading, setFxLoading]     = useState(true);
  const [fxError, setFxError]         = useState('');
  const [lastRefresh, setLastRefresh] = useState(null);

  // Load FX-only data (home currency preference + per-base rate snapshots).
  // Accounts come from AccountContext above.
  const loadFxData = useCallback(async (currentAccounts) => {
    setFxLoading(true);
    setFxError('');

    try {
      const homeCurrencyRes = await api.get('/currency/home');
      const fetchedHomeCurrency = homeCurrencyRes.data?.homeCurrency || 'EUR';
      setHomeCurrency(fetchedHomeCurrency);
      localStorage.setItem('sk_homeCurrency', fetchedHomeCurrency);

      const accountCurrencies = [
        ...new Set((currentAccounts || []).map(a => a.currency || 'EUR'))
      ].filter(c => c !== fetchedHomeCurrency);

      if (accountCurrencies.length > 0) {
        const ratePromises = accountCurrencies.map(c =>
          api.get('/currency/rates', { params: { base: c } })
            .then(r => ({ base: c, rates: r.data?.rates || {} }))
            .catch(() => ({ base: c, rates: {} }))
        );
        const rateResults = await Promise.all(ratePromises);
        const ratesMap = {};
        rateResults.forEach(({ base, rates: r }) => { ratesMap[base] = r; });
        setRates(ratesMap);
      } else {
        setRates({});
      }

      setLastRefresh(new Date());
    } catch (err) {
      // FX failure is non-fatal — we still render accounts in their native
      // currencies; only the consolidated home-currency total is affected.
      const msg = err.response?.data?.message || 'Failed to load FX rates.';
      setFxError(msg);
    } finally {
      setFxLoading(false);
    }
  }, []);

  // Re-load FX whenever the accounts list changes (so new currencies get rates)
  useEffect(() => {
    if (!accountsLoading) {
      loadFxData(accounts);
    }
  }, [accounts, accountsLoading, loadFxData]);

  // Combined refresh handler for the refresh button
  const handleRefresh = useCallback(() => {
    loadFxData(accounts);
    window.dispatchEvent(new Event('soldikeeper:accounts-refresh'));
  }, [accounts, loadFxData]);

  const loading = accountsLoading || fxLoading;
  // Only show the dashboard's own error banner if FX failed AND we have no
  // accounts to fall back on. AccountContext errors are surfaced by AccountsPage.
  const error = fxError && accounts.length === 0 ? fxError : '';

  // Convert an amount from one currency to homeCurrency using cached rates
  const convertToHome = useCallback((amount, fromCurrency) => {
    if (fromCurrency === homeCurrency) return amount;
    const fromRates = rates[fromCurrency];
    if (!fromRates || !fromRates[homeCurrency]) return null;
    return Math.round(amount * fromRates[homeCurrency] * 100) / 100;
  }, [rates, homeCurrency]);

  // ── Compute consolidated totals ────────────────────────────────────────────
  const grouped = groupByCurrency(accounts);

  // For each currency group, sum balances and convert
  const currencyGroups = Object.entries(grouped).map(([currency, accs]) => {
    const nativeTotal = accs.reduce((sum, a) => {
      // Use computed balance if present, otherwise initialBalance
      const bal = a.balance !== undefined ? a.balance : (a.initialBalance || 0);
      return sum + bal;
    }, 0);
    const homeEquivalent = convertToHome(nativeTotal, currency);
    return { currency, accounts: accs, nativeTotal, homeEquivalent };
  });

  // Grand total: sum of all homeEquivalent values (only those fully converted)
  const grandTotal = currencyGroups.reduce((sum, g) => {
    if (g.homeEquivalent === null) return sum;
    return sum + g.homeEquivalent;
  }, 0);

  const hasPartialConversion = currencyGroups.some(g => g.homeEquivalent === null);

  // ── Render ─────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" action={
        <IconButton size="small" onClick={handleRefresh}><RefreshIcon fontSize="inherit" /></IconButton>
      }>
        {error}
      </Alert>
    );
  }

  // Defensive: only show "no accounts" empty state when we have genuinely
  // finished loading without errors AND the shared accounts list is empty.
  // (Was previously rendered whenever the local /accounts fetch failed.)
  if (!accountsLoading && !accountsError && accounts.length === 0) {
    return (
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <AccountBalanceWalletIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography variant="body1" color="text.secondary">
            No accounts found. Add an account to see your multi-currency wallet.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  // If accounts errored upstream, render nothing here (AccountsPage shows the error).
  if (accounts.length === 0) {
    return null;
  }

  return (
    <Card variant="outlined" sx={{ borderRadius: 2 }}>
      <CardContent sx={{ p: 3 }}>
        {/* Header */}
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <AccountBalanceWalletIcon color="primary" />
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Multi-Currency Wallet
              </Typography>
              <Typography variant="caption" color="text.secondary">
                All accounts consolidated in {homeCurrency}
              </Typography>
            </Box>
          </Box>
          <Tooltip title={`Last refreshed: ${lastRefresh ? lastRefresh.toLocaleTimeString() : '—'}`}>
            <IconButton size="small" onClick={handleRefresh} disabled={loading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Currency groups */}
        <Stack spacing={2}>
          {currencyGroups.map(({ currency, accounts: accs, nativeTotal, homeEquivalent }) => (
            <Box key={currency}>
              {/* Currency group header */}
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Chip
                  label={currency}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ fontWeight: 700, fontFamily: 'monospace' }}
                />
                <Typography variant="body2" color="text.secondary">
                  {accs.length} account{accs.length !== 1 ? 's' : ''}
                </Typography>
              </Box>

              {/* Individual accounts in this currency */}
              <Stack spacing={0.5} pl={1}>
                {accs.map((account) => {
                  const bal = account.balance !== undefined ? account.balance : (account.initialBalance || 0);
                  const isLiability = ['credit', 'creditcard', 'loan', 'mortgage'].includes(account.type);
                  return (
                    <Box
                      key={account._id}
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{
                        px: 1.5,
                        py: 0.75,
                        borderRadius: 1,
                        bgcolor: 'action.hover',
                        '&:hover': { bgcolor: 'action.selected' }
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2" sx={{ mr: 0.5 }}>
                          {account.icon || '🏦'}
                        </Typography>
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            {account.name}
                          </Typography>
                          <Chip
                            label={account.type}
                            size="small"
                            color={ACCOUNT_TYPE_COLORS[account.type] || 'default'}
                            variant="outlined"
                            sx={{ height: 16, fontSize: '0.6rem', mt: 0.25 }}
                          />
                        </Box>
                      </Box>
                      <Box textAlign="right">
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          color={isLiability ? 'error.main' : bal >= 0 ? 'success.main' : 'error.main'}
                        >
                          {formatAmount(bal, currency)}
                        </Typography>
                        {currency !== homeCurrency && convertToHome(bal, currency) !== null && (
                          <Typography variant="caption" color="text.secondary">
                            ≈ {formatAmount(convertToHome(bal, currency), homeCurrency)}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Stack>

              {/* Currency group subtotal */}
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                px={1.5}
                pt={0.75}
              >
                <Typography variant="caption" color="text.secondary">
                  Subtotal {currency}
                </Typography>
                <Box textAlign="right">
                  <Typography variant="body2" fontWeight={600}>
                    {formatAmount(nativeTotal, currency)}
                  </Typography>
                  {currency !== homeCurrency && homeEquivalent !== null && (
                    <Typography variant="caption" color="text.secondary">
                      ≈ {formatAmount(homeEquivalent, homeCurrency)}
                    </Typography>
                  )}
                  {currency !== homeCurrency && homeEquivalent === null && (
                    <Typography variant="caption" color="warning.main">
                      Rate unavailable
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          ))}
        </Stack>

        <Divider sx={{ my: 2 }} />

        {/* Grand total */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            px: 2,
            py: 1.5,
            borderRadius: 2,
            bgcolor: 'primary.main',
            color: 'primary.contrastText'
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <CurrencyExchangeIcon fontSize="small" />
            <Typography variant="subtitle1" fontWeight={700}>
              Total (in {homeCurrency})
            </Typography>
          </Box>
          <Box textAlign="right">
            <Typography variant="h6" fontWeight={700}>
              {formatAmount(grandTotal, homeCurrency)}
            </Typography>
            {hasPartialConversion && (
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                * some rates unavailable
              </Typography>
            )}
          </Box>
        </Box>

        {/* Footer info */}
        <Typography variant="caption" color="text.secondary" display="block" mt={1.5}>
          Exchange rates sourced from frankfurter.app · Cached 1 h · Indicative only
        </Typography>
      </CardContent>
    </Card>
  );
};

export default MultiCurrencyDashboard;
